import { orchApiSlice } from '../apiSlice';

// ─── Request Types ─────────────────────────────────────────

export interface ICreateJobRequest {
	idempotencyKey: string | null;
	jobType: string | null;
	serviceType: string | null;
	/**
	 * The wallet to bill. Omitted for a team member, who has none: once api.core authorises spends
	 * (always for a team member) api.orchestration ignores this and bills the wallet core names.
	 */
	walletId?: string;
	initiatedByUserId: string;
	items: {
		externalReference: string | null;
		payloadJson: string;
	}[];
}

/**
 * Pre-send unit calculation. Prices a message WITHOUT creating a job or touching the wallet.
 *
 * `mediaKind` is only `image` | `video`; anything else is sent without it and the server falls back
 * to 2 units per attachment. Path-guessing (`/video/upload/`) is disabled server-side, so a storage
 * URL with no clean extension must carry `mediaKind` or it is priced as the fallback.
 */
export interface ICalculateUnitsRequest {
	jobType: 'SMS' | 'EMAIL' | 'WHATSAPP';
	message?: string;
	media?: string[];
	mediaKind?: 'image' | 'video';
	/** Defaults to 1 server-side. */
	recipientCount?: number;
}

// ─── Response Types ────────────────────────────────────────

/** Bare DTO — like every other OrchestrationJobs response, no envelope. */
export interface ICalculateUnitsResponse {
	jobType: string;
	unitsPerRecipient: number;
	recipientCount: number;
	/** unitsPerRecipient × recipientCount. What `/usage/validate` is asked to cover. */
	totalUnits: number;
	characterCount: number;
	/** SMS: one non-GSM-7 character (e.g. an emoji) flips the whole message to 70/67 per segment. */
	isUnicode: boolean;
	segmentCount: number;
	mediaCount: number;
	imageCount: number;
	videoCount: number;
	otherMediaCount: number;
	/** e.g. `GSM-7`, `UCS-2`, `WHATSAPP-MEDIA`. */
	encoding: string;
	segmentLimit: number;
	charactersRemainingInSegment: number;
}

export interface ISubmitJobResponse {
	jobId: string;
	status: string;
	statusUrl: string;
}

export interface IJobStatusResponse {
	jobId: string;
	jobType: string;
	serviceType: string;
	status: string;
	totalItems: number;
	successCount: number;
	failureCount: number;
	pendingCount: number;
	walletReservationReference: string | null;
	createdAtUtc: string;
	completedAtUtc: string | null;
	failureReason: string | null;
	/**
	 * Who sent it, when api.core authorised the spend: "S2 – James Adamu" for a team member, the
	 * wallet holder's name otherwise. Absent on jobs (and builds) from before team access P3.
	 */
	spendActorLabel?: string | null;
	/** The team member's support number when a team member sent it. */
	spendSupportNumber?: number | null;
}

export interface IPagedResponse<T> {
	items: T[];
	page: number;
	pageSize: number;
	totalCount: number;
	totalPages: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
}

export interface IJobItemResponse {
	itemId: string;
	sequence: number;
	externalReference: string;
	status: string;
	attemptCount: number;
	nextRetryAtUtc: string | null;
	processedAtUtc: string | null;
	lastError: string | null;
}

// ─── Endpoints ─────────────────────────────────────────────

const jobsApiSlice = orchApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		createJob: builder.mutation<ISubmitJobResponse, ICreateJobRequest>({
			query: (body) => ({
				method: 'POST',
				url: '/orchestration/OrchestrationJobs',
				body,
			}),
		}),
		getJob: builder.query<IJobStatusResponse, string>({
			query: (jobId) => `/orchestration/OrchestrationJobs/${jobId}`,
		}),
		getJobItems: builder.query<
			IPagedResponse<IJobItemResponse>,
			{ jobId: string; status?: string | null; page?: number; pageSize?: number }
		>({
			query: ({ jobId, status, page = 1, pageSize = 50 }) => ({
				url: `/orchestration/OrchestrationJobs/${jobId}/items`,
				params: { page, pageSize, status },
			}),
		}),
		getJobsByUser: builder.query<
			IPagedResponse<IJobStatusResponse>,
			{ userId: string; page?: number; pageSize?: number; status?: string; jobType?: string }
		>({
			query: ({ userId, page = 1, pageSize = 50, status, jobType }) => ({
				url: `/orchestration/OrchestrationJobs/by-user/${userId}`,
				params: {
					page,
					pageSize,
					...(status ? { status } : {}),
					...(jobType ? { jobType } : {}),
				},
			}),
		}),
		// Jobs billed to a wallet, whoever ran them — so an owner sees the jobs their team sent.
		// Team access P3; an api.orchestration that predates it answers 404 (callers fall back to by-user).
		getJobsByWallet: builder.query<
			IPagedResponse<IJobStatusResponse>,
			{ walletId: string; page?: number; pageSize?: number; status?: string; jobType?: string }
		>({
			query: ({ walletId, page = 1, pageSize = 50, status, jobType }) => ({
				url: `/orchestration/OrchestrationJobs/by-wallet/${walletId}`,
				params: {
					page,
					pageSize,
					...(status ? { status } : {}),
					...(jobType ? { jobType } : {}),
				},
			}),
		}),
		// A query, not a mutation, although it is a POST: it has no side effect, so identical
		// requests are served from cache and `isFetching` says whether the figure is current.
		calculateUnits: builder.query<ICalculateUnitsResponse, ICalculateUnitsRequest>({
			query: (body) => ({
				method: 'POST',
				url: '/orchestration/OrchestrationJobs/calculate-units',
				body,
			}),
		}),
		retryFailedJob: builder.mutation<IJobStatusResponse, string>({
			query: (jobId) => ({
				method: 'POST',
				url: `/orchestration/OrchestrationJobs/${jobId}/retry-failed`,
			}),
		}),
	}),
});

export const {
	useCreateJobMutation,
	useGetJobQuery,
	useLazyGetJobQuery,
	useGetJobItemsQuery,
	useLazyGetJobItemsQuery,
	useGetJobsByUserQuery,
	useGetJobsByWalletQuery,
	useRetryFailedJobMutation,
	useCalculateUnitsQuery,
} = jobsApiSlice;
